/**
 * @breathly/web — API client.
 *
 * A thin fetch wrapper over the Breathly FastAPI backend. Per the technical
 * constitution (API_GUIDELINES.md): resources are named after domain concepts,
 * the API is versioned, and errors are typed.
 *
 * This client is intentionally minimal — no caching, no retries, no interceptors.
 * Complexity is earned as real needs emerge (09_STARTUP_PHILOSOPHY.md).
 */

import { apiBasePath, type ApiConfig } from '@breathly/config';

/** Default API config from environment. */
const defaultConfig: ApiConfig = {
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? 'https://api.fulgent.digital',
  version: 'v1',
};

/** Prefix used for the Authorization header. */
const BEARER = 'Bearer';

// ---------------------------------------------------------------------------
// Types (mirrors @breathly/shared contracts for client convenience)
// ---------------------------------------------------------------------------

export interface EventResponse {
  readonly id: string;
  readonly user_id: string;
  readonly kind: string;
  readonly occurred_at: string;
  readonly ended_at: string | null;
  readonly payload: Record<string, unknown>;
  readonly context: string | null;
  readonly is_future: boolean;
  readonly archived_at: string | null;
  readonly recorded_at: string;
  readonly updated_at: string;
}

export interface EventCreateRequest {
  readonly kind: string;
  readonly occurred_at: string;
  readonly ended_at?: string | null;
  readonly payload?: Record<string, unknown>;
  readonly context?: string | null;
  readonly is_future?: boolean;
}

export interface EventUpdateRequest {
  readonly payload?: Record<string, unknown> | null;
  readonly context?: string | null;
  readonly is_future?: boolean | null;
}

export interface EventListResponse {
  readonly items: readonly EventResponse[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

export type ConfidenceLevel = 'very-low' | 'low' | 'moderate' | 'high' | 'very-high';

export type ObservationOrigin = 'system' | 'user';

export interface ObservationResponse {
  readonly id: string;
  readonly user_id: string;
  readonly statement: string;
  readonly evidence_event_ids: readonly string[];
  readonly confidence: ConfidenceLevel;
  readonly confidence_basis: string | null;
  readonly origin: ObservationOrigin;
  readonly superseded_by: string | null;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ObservationCreateRequest {
  readonly statement: string;
  readonly evidence_event_ids?: readonly string[];
  readonly confidence?: ConfidenceLevel;
  readonly confidence_basis?: string | null;
  readonly origin?: 'user';
}

export interface ObservationDraftResponse {
  readonly statement: string;
  readonly evidence_event_ids: readonly string[];
  readonly confidence: ConfidenceLevel;
  readonly confidence_basis: string;
  readonly origin: 'system';
}

export interface ObservationListResponse {
  readonly items: readonly ObservationResponse[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

export interface ObservationDraftListResponse {
  readonly items: readonly ObservationDraftResponse[];
}

export type KnowledgeOrigin = 'system' | 'user';
export type KnowledgeCandidateSource = 'observations' | 'experiments';

export interface KnowledgeResponse {
  readonly id: string;
  readonly user_id: string;
  readonly statement: string;
  readonly evidence_event_ids: readonly string[];
  readonly evidence_observation_ids: readonly string[];
  readonly evidence_experiment_ids: readonly string[];
  readonly confidence: ConfidenceLevel;
  readonly confidence_basis: string | null;
  readonly origin: KnowledgeOrigin;
  readonly superseded_by: string | null;
  readonly stale_at: string | null;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface KnowledgeCreateRequest {
  readonly statement: string;
  readonly evidence_event_ids?: readonly string[];
  readonly evidence_observation_ids?: readonly string[];
  readonly evidence_experiment_ids?: readonly string[];
  readonly confidence?: ConfidenceLevel;
  readonly confidence_basis?: string | null;
  readonly origin?: 'user';
}

export interface KnowledgeDraftResponse {
  readonly statement: string;
  readonly evidence_event_ids: readonly string[];
  readonly evidence_observation_ids: readonly string[];
  readonly evidence_experiment_ids: readonly string[];
  readonly confidence: ConfidenceLevel;
  readonly confidence_basis: string;
  readonly origin: 'system';
  readonly candidate_source: KnowledgeCandidateSource;
}

export interface KnowledgeCandidateDismissResponse {
  readonly id: string;
  readonly user_id: string;
  readonly statement: string;
  readonly normalized_statement: string;
  readonly candidate_source: KnowledgeCandidateSource;
  readonly dismissed_at: string;
}

export interface KnowledgeListResponse {
  readonly items: readonly KnowledgeResponse[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

export interface KnowledgeDraftListResponse {
  readonly items: readonly KnowledgeDraftResponse[];
}

export type PlanItemStatus = 'proposed' | 'accepted' | 'declined' | 'done';
export type PlanOrigin = 'system' | 'user';

export interface PlanItemResponse {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly basis: string;
  readonly status: PlanItemStatus;
}

export interface DailyPlanDraftResponse {
  readonly plan_date: string;
  readonly summary: string;
  readonly items: readonly PlanItemResponse[];
  readonly basis_event_ids: readonly string[];
  readonly basis_observation_ids: readonly string[];
  readonly basis_knowledge_ids: readonly string[];
  readonly origin: 'system';
}

export interface DailyPlanResponse {
  readonly id: string;
  readonly user_id: string;
  readonly plan_date: string;
  readonly summary: string;
  readonly items: readonly PlanItemResponse[];
  readonly basis_event_ids: readonly string[];
  readonly basis_observation_ids: readonly string[];
  readonly basis_knowledge_ids: readonly string[];
  readonly origin: PlanOrigin;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export type ExperimentStatus = 'proposed' | 'active' | 'completed' | 'abandoned';
export type ExperimentOutcome = 'supported' | 'unsupported' | 'inconclusive' | 'abandoned';
export type ExperimentOrigin = 'system' | 'user';

export interface ExperimentResponse {
  readonly id: string;
  readonly user_id: string;
  readonly hypothesis: string;
  readonly purpose: string;
  readonly protocol: string;
  readonly observation_target: string;
  readonly start_date: string;
  readonly end_date: string;
  readonly status: ExperimentStatus;
  readonly outcome: ExperimentOutcome | null;
  readonly outcome_notes: string | null;
  readonly confidence: ConfidenceLevel;
  readonly confidence_basis: string | null;
  readonly basis_event_ids: readonly string[];
  readonly basis_observation_ids: readonly string[];
  readonly basis_knowledge_ids: readonly string[];
  readonly origin: ExperimentOrigin;
  readonly concluded_at: string | null;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ExperimentCreateRequest {
  readonly hypothesis: string;
  readonly purpose: string;
  readonly protocol: string;
  readonly observation_target: string;
  readonly start_date: string;
  readonly end_date: string;
  readonly confidence?: ConfidenceLevel;
  readonly confidence_basis?: string | null;
  readonly basis_event_ids?: readonly string[];
  readonly basis_observation_ids?: readonly string[];
  readonly basis_knowledge_ids?: readonly string[];
  readonly origin?: 'user';
}

export interface ExperimentListResponse {
  readonly items: readonly ExperimentResponse[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

export interface AttachmentUploadResponse {
  readonly id: string;
  readonly key: string;
  readonly filename: string;
  readonly content_type: string;
  readonly size: number;
  readonly evidence_type: string;
  readonly uploaded_at: string;
  readonly download_path: string;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export interface ApiError {
  readonly status: number;
  readonly detail: string;
  readonly code?: string;
}

export class ApiClient {
  readonly config: ApiConfig;

  /** In-memory token; the auth system sets this after login. */
  private _token: string | null = null;

  constructor(config: ApiConfig = defaultConfig) {
    this.config = config;
  }

  // -- Token management ---------------------------------------------------

  get token(): string | null {
    return this._token;
  }

  setToken(token: string | null): void {
    this._token = token;
  }

  clearToken(): void {
    this._token = null;
  }

  // -- Core request -------------------------------------------------------

  private async _request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const base = apiBasePath(this.config);
    const url = `${base}${path}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this._token) {
      headers['Authorization'] = `${BEARER} ${this._token}`;
    }

    const response = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }).catch((err: unknown) => {
      const message = err instanceof Error ? err.message : 'Network request failed';
      throw {
        status: 0,
        detail: `Cannot reach Breathly API at ${this.config.baseUrl}. ${message}`,
      } satisfies ApiError;
    });

    if (!response.ok) {
      let detail = response.statusText;
      try {
        const errorBody = (await response.json()) as Record<string, unknown>;
        if (typeof errorBody.detail === 'string') {
          detail = errorBody.detail;
        }
      } catch {
        // Response body is not JSON; use status text.
      }
      throw { status: response.status, detail } satisfies ApiError;
    }

    // 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  private async _upload<T>(path: string, form: FormData): Promise<T> {
    const base = apiBasePath(this.config);
    const url = `${base}${path}`;
    const headers: Record<string, string> = {};
    if (this._token) headers.Authorization = `${BEARER} ${this._token}`;

    const response = await fetch(url, { method: 'POST', headers, body: form }).catch(
      (err: unknown) => {
        const message = err instanceof Error ? err.message : 'Network request failed';
        throw {
          status: 0,
          detail: `Cannot reach Breathly API at ${this.config.baseUrl}. ${message}`,
        } satisfies ApiError;
      },
    );

    if (!response.ok) {
      let detail = response.statusText;
      try {
        const errorBody = (await response.json()) as Record<string, unknown>;
        if (typeof errorBody.detail === 'string') detail = errorBody.detail;
      } catch {
        // Response body is not JSON; use status text.
      }
      throw { status: response.status, detail } satisfies ApiError;
    }
    return (await response.json()) as T;
  }

  // -- HTTP verbs ---------------------------------------------------------

  get<T>(path: string): Promise<T> {
    return this._request<T>('GET', path);
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this._request<T>('POST', path, body);
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this._request<T>('PUT', path, body);
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this._request<T>('PATCH', path, body);
  }

  del(path: string): Promise<void> {
    return this._request<void>('DELETE', path);
  }

  // -- Endpoint helpers ---------------------------------------------------

  // -- Health -------------------------------------------------------------

  /** Fetch health status (unauthenticated). */
  async health(): Promise<{ status: string; service: string; timestamp: string }> {
    return this.get('/health');
  }

  // -- Auth ---------------------------------------------------------------

  /** Fetch the authenticated user's identity (requires token). */
  async me(): Promise<{ user_id: string }> {
    return this.get('/me');
  }

  async uploadAttachment(file: File, evidenceType: string): Promise<AttachmentUploadResponse> {
    const form = new FormData();
    form.set('file', file);
    form.set('evidence_type', evidenceType);
    return this._upload('/attachments', form);
  }

  // -- Events -------------------------------------------------------------

  /** Create a new event (POST /events). */
  async createEvent(body: EventCreateRequest): Promise<EventResponse> {
    return this.post('/events', body);
  }

  /** List events with optional cursor pagination, kind filter, and date range (GET /events). */
  /** Archive (soft-delete) an event by ID (POST /events/{id}/archive). */
  async archiveEvent(eventId: string): Promise<EventResponse> {
    return this.post(`/events/${encodeURIComponent(eventId)}/archive`);
  }

  /** Restore an archived event (POST /events/{id}/restore). */
  async restoreEvent(eventId: string): Promise<EventResponse> {
    return this.post(`/events/${encodeURIComponent(eventId)}/restore`);
  }

  async listEvents(params?: {
    cursor?: string;
    kind?: string;
    limit?: number;
    occurred_at_gte?: string;
    occurred_at_lte?: string;
    include_archived?: boolean;
  }): Promise<EventListResponse> {
    const query = new URLSearchParams();
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.kind) query.set('kind', params.kind);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.occurred_at_gte) query.set('occurred_at_gte', params.occurred_at_gte);
    if (params?.occurred_at_lte) query.set('occurred_at_lte', params.occurred_at_lte);
    if (params?.include_archived) query.set('include_archived', 'true');
    const qs = query.toString();
    return this.get(`/events${qs ? `?${qs}` : ''}`);
  }

  /** Get a single event by ID (GET /events/{id}). */
  async getEvent(eventId: string): Promise<EventResponse> {
    return this.get(`/events/${encodeURIComponent(eventId)}`);
  }

  /** Update mutable event metadata (PUT /events/{id}). */
  async updateEvent(eventId: string, body: EventUpdateRequest): Promise<EventResponse> {
    return this.put(`/events/${encodeURIComponent(eventId)}`, body);
  }

  /** Delete an event by ID (DELETE /events/{id}). */
  async deleteEvent(eventId: string): Promise<void> {
    return this.del(`/events/${encodeURIComponent(eventId)}`);
  }

  // -- Observations -------------------------------------------------------

  /** Create a user-authored observation (POST /observations). */
  async createObservation(body: ObservationCreateRequest): Promise<ObservationResponse> {
    return this.post('/observations', body);
  }

  /** List observations with cursor pagination and optional filters. */
  async listObservations(params?: {
    cursor?: string;
    origin?: ObservationOrigin;
    limit?: number;
    include_archived?: boolean;
  }): Promise<ObservationListResponse> {
    const query = new URLSearchParams();
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.origin) query.set('origin', params.origin);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.include_archived) query.set('include_archived', 'true');
    const qs = query.toString();
    return this.get(`/observations${qs ? `?${qs}` : ''}`);
  }

  /** Get a single observation by ID. */
  async getObservation(observationId: string): Promise<ObservationResponse> {
    return this.get(`/observations/${encodeURIComponent(observationId)}`);
  }

  /** Archive an observation by ID. */
  async archiveObservation(observationId: string): Promise<ObservationResponse> {
    return this.post(`/observations/${encodeURIComponent(observationId)}/archive`);
  }

  /** Preview generated observation candidates from recent events. */
  async previewObservations(params?: { limit?: number }): Promise<ObservationDraftListResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return this.get(`/observations/preview${qs ? `?${qs}` : ''}`);
  }

  // -- Knowledge ----------------------------------------------------------

  /** Create a user-authored Knowledge item (POST /knowledge). */
  async createKnowledge(body: KnowledgeCreateRequest): Promise<KnowledgeResponse> {
    return this.post('/knowledge', body);
  }

  /** List Knowledge with cursor pagination and optional filters. */
  async listKnowledge(params?: {
    cursor?: string;
    origin?: KnowledgeOrigin;
    limit?: number;
    include_archived?: boolean;
    include_stale?: boolean;
  }): Promise<KnowledgeListResponse> {
    const query = new URLSearchParams();
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.origin) query.set('origin', params.origin);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.include_archived) query.set('include_archived', 'true');
    if (params?.include_stale === false) query.set('include_stale', 'false');
    const qs = query.toString();
    return this.get(`/knowledge${qs ? `?${qs}` : ''}`);
  }

  /** Get a single Knowledge item by ID. */
  async getKnowledge(knowledgeId: string): Promise<KnowledgeResponse> {
    return this.get(`/knowledge/${encodeURIComponent(knowledgeId)}`);
  }

  /** Archive a Knowledge item by ID. */
  async archiveKnowledge(knowledgeId: string): Promise<KnowledgeResponse> {
    return this.post(`/knowledge/${encodeURIComponent(knowledgeId)}/archive`);
  }

  /** Preview generated Knowledge candidates from saved observations. */
  async previewKnowledge(params?: { limit?: number }): Promise<KnowledgeDraftListResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return this.get(`/knowledge/preview${qs ? `?${qs}` : ''}`);
  }

  /** Commit a backend-derived Knowledge candidate by statement. */
  async commitKnowledgeCandidate(statement: string): Promise<KnowledgeResponse> {
    return this.post('/knowledge/commit', { statement });
  }

  async dismissKnowledgeCandidate(
    statement: string,
    candidateSource: KnowledgeCandidateSource,
  ): Promise<KnowledgeCandidateDismissResponse> {
    return this.post('/knowledge/candidates/dismiss', {
      statement,
      candidate_source: candidateSource,
    });
  }

  async previewExperimentLearning(params?: {
    limit?: number;
  }): Promise<KnowledgeDraftListResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return this.get(`/knowledge/experiment-learning/preview${qs ? `?${qs}` : ''}`);
  }

  async commitExperimentLearning(statement: string): Promise<KnowledgeResponse> {
    return this.post('/knowledge/experiment-learning/commit', { statement });
  }

  // -- Plans --------------------------------------------------------------

  async getTodayPlan(params?: {
    plan_date?: string;
  }): Promise<DailyPlanDraftResponse | DailyPlanResponse> {
    const query = new URLSearchParams();
    if (params?.plan_date) query.set('plan_date', params.plan_date);
    const qs = query.toString();
    return this.get(`/plans/today${qs ? `?${qs}` : ''}`);
  }

  async commitTodayPlan(planDate: string): Promise<DailyPlanResponse> {
    return this.post('/plans/today/commit', { plan_date: planDate });
  }

  async updatePlanItemStatus(
    planId: string,
    itemId: string,
    status: PlanItemStatus,
  ): Promise<DailyPlanResponse> {
    return this.post(
      `/plans/${encodeURIComponent(planId)}/items/${encodeURIComponent(itemId)}/status`,
      { status },
    );
  }

  // -- Experiments --------------------------------------------------------

  async createExperiment(body: ExperimentCreateRequest): Promise<ExperimentResponse> {
    return this.post('/experiments', body);
  }

  async listExperiments(params?: {
    cursor?: string;
    status?: ExperimentStatus;
    limit?: number;
    include_archived?: boolean;
  }): Promise<ExperimentListResponse> {
    const query = new URLSearchParams();
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.include_archived) query.set('include_archived', 'true');
    const qs = query.toString();
    return this.get(`/experiments${qs ? `?${qs}` : ''}`);
  }

  async updateExperimentStatus(
    experimentId: string,
    body: {
      status: ExperimentStatus;
      outcome?: ExperimentOutcome | null;
      outcome_notes?: string | null;
    },
  ): Promise<ExperimentResponse> {
    return this.post(`/experiments/${encodeURIComponent(experimentId)}/status`, body);
  }

  async archiveExperiment(experimentId: string): Promise<ExperimentResponse> {
    return this.post(`/experiments/${encodeURIComponent(experimentId)}/archive`);
  }
}

/** Singleton API client for the web app. */
export const api = new ApiClient();
